import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';

import { compareRepresentativeRelationships } from '@entangled-vega/shared';

import { type GraphPerson, type GraphRelationship } from '../data/graph-types.js';
import {
  adjacentDossierSheetState,
  clampDossierRect,
  defaultFloatingDossierRect,
  dossierSheetHeight,
  dossierRectsEqual,
  DOSSIER_LAYOUT_CHANGE_EVENT,
  DOSSIER_SHEET_STATES,
  isNarrowDossierViewport,
  moveDossierRect,
  preferDossierSheetState,
  resizeDossierRect,
  type DossierRect,
  type DossierSheetState,
  type DossierViewport,
  type DossierWorkspaceState,
} from '../lib/dossier-workspace.js';
import { type CounterpartConnection, type GraphIndex } from '../lib/graph.js';
import {
  groupSourcesForDisplay,
  relationshipProvenanceLabel,
  relationshipTone,
  sourcesForRelationship,
} from '../lib/provenance.js';
import {
  FOCUS_DEPTHS,
  FOCUS_DEPTH_LABELS,
  RELATIONSHIP_FILTERS,
  RELATIONSHIP_FILTER_LABELS,
  relationshipFilterForPerson,
  relationshipLabelForPerson,
  relationshipMatchesFilters,
  usefulRelationshipNote,
  type FocusDepth,
  type RelationshipFilter,
} from '../lib/relationship-view.js';
import { relationshipDateLabel } from '../lib/relationship-time.js';
import type { PersonConstellationSummary } from '../lib/story-graph-models.js';

type PersonPanelProps = {
  person: GraphPerson;
  index: GraphIndex;
  fixture: boolean;
  snapshotDate: string;
  limitations?: readonly string[] | undefined;
  constellationSummary?: PersonConstellationSummary | null | undefined;
  suggestions: readonly GraphPerson[];
  enabledRelationshipFilters: readonly RelationshipFilter[];
  focusDepth: FocusDepth;
  favorite: boolean;
  onToggleFavorite: () => void;
  onClose: () => void;
  canGoBack: boolean;
  onBack: () => void;
  onSelect: (personId: string) => void;
  onConnect: (personId: string) => void;
  onSetRelationshipFilters: (filters: readonly RelationshipFilter[]) => void;
  onSetFocusDepth: (depth: FocusDepth) => void;
  workspace: DossierWorkspaceState;
  onWorkspaceChange: (workspace: DossierWorkspaceState) => void;
  viewMode: 'graph' | 'list';
  onViewModeChange: (view: 'graph' | 'list') => void;
};

type VisibleCounterpart = {
  counterpart: CounterpartConnection;
  phases: readonly GraphRelationship[];
  representative: GraphRelationship;
  relationshipLabels: readonly string[];
};

type WorkspaceInteraction = {
  kind: 'move' | 'resize';
  pointerId: number;
  startX: number;
  startY: number;
  startRect: DossierRect;
};

type SheetInteraction = {
  pointerId: number;
  startY: number;
  startState: DossierSheetState;
  currentState: DossierSheetState;
};

type NarrowWorkspaceMetrics = {
  active: boolean;
  availableHeight: number;
};

type WorkspaceIconName =
  'close' | 'dock' | 'expand' | 'float' | 'graph' | 'list' | 'minimize' | 'pin' | 'reset' | 'unpin';

function WorkspaceIcon({ name }: { name: WorkspaceIconName }) {
  let content: ReactNode;
  switch (name) {
    case 'close':
      content = (
        <>
          <path d="M6 6l12 12" />
          <path d="M18 6L6 18" />
        </>
      );
      break;
    case 'dock':
      content = (
        <>
          <rect x="4" y="4" width="16" height="16" rx="2" />
          <path d="M15 4v16" />
        </>
      );
      break;
    case 'expand':
      content = (
        <>
          <path d="M7 10l5 5 5-5" />
          <path d="M5 5h14" />
        </>
      );
      break;
    case 'float':
      content = (
        <>
          <rect x="4" y="6" width="13" height="13" rx="2" />
          <path d="M13 4h7v7" />
          <path d="M20 4l-8 8" />
        </>
      );
      break;
    case 'graph':
      content = (
        <>
          <circle cx="6" cy="12" r="2" />
          <circle cx="18" cy="7" r="2" />
          <circle cx="18" cy="17" r="2" />
          <path d="M8 11l8-3" />
          <path d="M8 13l8 3" />
        </>
      );
      break;
    case 'list':
      content = (
        <>
          <path d="M9 7h10" />
          <path d="M9 12h10" />
          <path d="M9 17h10" />
          <path d="M5 7h.01" />
          <path d="M5 12h.01" />
          <path d="M5 17h.01" />
        </>
      );
      break;
    case 'minimize':
      content = (
        <>
          <path d="M5 8h14" />
          <path d="M7 13l5 5 5-5" />
        </>
      );
      break;
    case 'pin':
      content = (
        <>
          <path d="M9 4h6l-1 5 3 3H7l3-3-1-5z" />
          <path d="M12 12v8" />
        </>
      );
      break;
    case 'reset':
      content = (
        <>
          <path d="M5 9a8 8 0 111 8" />
          <path d="M5 4v5h5" />
        </>
      );
      break;
    case 'unpin':
      content = (
        <>
          <path d="M9 4h6l-1 5 3 3H9" />
          <path d="M12 12v8" />
          <path d="M4 4l16 16" />
        </>
      );
      break;
  }
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      {content}
    </svg>
  );
}

function visibleViewport(): DossierViewport {
  const visualViewport = window.visualViewport;
  const viewport =
    visualViewport !== null
      ? {
          left: visualViewport.offsetLeft,
          top: visualViewport.offsetTop,
          width: visualViewport.width,
          height: visualViewport.height,
        }
      : {
          left: 0,
          top: 0,
          width: document.documentElement.clientWidth,
          height: document.documentElement.clientHeight,
        };
  const rootStyle = window.getComputedStyle(document.documentElement);
  const safeInset = (property: string) => {
    const value = Number.parseFloat(rootStyle.getPropertyValue(property));
    return Number.isFinite(value) ? Math.max(0, value) : 0;
  };
  const top = safeInset('--safe-area-top');
  const right = safeInset('--safe-area-right');
  const bottom = safeInset('--safe-area-bottom');
  const left = safeInset('--safe-area-left');
  return {
    left: viewport.left + left,
    top: viewport.top + top,
    width: Math.max(1, viewport.width - left - right),
    height: Math.max(1, viewport.height - top - bottom),
  };
}

function narrowWorkspaceMetrics(container: HTMLElement | null): NarrowWorkspaceMetrics {
  const viewport = visibleViewport();
  const containerRect = container?.getBoundingClientRect();
  const header = document.querySelector<HTMLElement>('.site-header');
  const headerBottom = header?.getBoundingClientRect().bottom ?? viewport.top;
  const viewportBottom = viewport.top + viewport.height;
  const availableLeft = Math.max(viewport.left, containerRect?.left ?? viewport.left);
  const availableRight = Math.min(
    viewport.left + viewport.width,
    containerRect?.right ?? viewport.left + viewport.width,
  );
  const availableTop = Math.max(viewport.top, headerBottom, containerRect?.top ?? viewport.top);
  const availableBottom = Math.min(viewportBottom, containerRect?.bottom ?? viewportBottom);
  const availableWidth = Math.max(1, availableRight - availableLeft);
  const availableHeight = Math.max(1, availableBottom - availableTop);
  return {
    active: isNarrowDossierViewport({
      left: availableLeft,
      top: availableTop,
      width: availableWidth,
      height: availableHeight,
    }),
    availableHeight,
  };
}

function elementRect(element: HTMLElement): DossierRect {
  const rect = element.getBoundingClientRect();
  return {
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height,
  };
}

function notifyDossierLayout(): void {
  window.requestAnimationFrame(() => {
    window.dispatchEvent(new Event(DOSSIER_LAYOUT_CHANGE_EVENT));
  });
}

function filteredCounterparts(
  counterparts: readonly CounterpartConnection[],
  personId: string,
  enabled: ReadonlySet<RelationshipFilter>,
): VisibleCounterpart[] {
  return counterparts.flatMap((counterpart) => {
    const phases = counterpart.connections
      .flatMap((connection) => connection.phases)
      .filter((phase) => relationshipMatchesFilters(phase, personId, enabled));
    const representative = [...phases].sort(compareRepresentativeRelationships)[0];
    if (representative === undefined) {
      return [];
    }
    return [
      {
        counterpart,
        phases,
        representative,
        relationshipLabels: [
          ...new Set(phases.map((phase) => relationshipLabelForPerson(phase, personId))),
        ],
      },
    ];
  });
}

export function PersonPanel({
  person,
  index,
  fixture,
  snapshotDate,
  limitations,
  constellationSummary = null,
  suggestions,
  enabledRelationshipFilters,
  focusDepth,
  favorite,
  onToggleFavorite,
  onClose,
  canGoBack,
  onBack,
  onSelect,
  onConnect,
  onSetRelationshipFilters,
  onSetFocusDepth,
  workspace,
  onWorkspaceChange,
  viewMode,
  onViewModeChange,
}: PersonPanelProps) {
  const panelRef = useRef<HTMLElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const originRef = useRef<HTMLElement | null>(null);
  const interactionRef = useRef<WorkspaceInteraction | null>(null);
  const sheetInteractionRef = useRef<SheetInteraction | null>(null);
  const interactionCleanupRef = useRef<(() => void) | null>(null);
  const liveRectRef = useRef<DossierRect | null>(workspace.rect);
  const filterListRef = useRef<HTMLDivElement>(null);
  const [liveRect, setLiveRect] = useState<DossierRect | null>(workspace.rect);
  const [workspaceStatus, setWorkspaceStatus] = useState('');
  const [filterContinuationVisible, setFilterContinuationVisible] = useState(false);
  const [narrowMetrics, setNarrowMetrics] = useState<NarrowWorkspaceMetrics>({
    active: false,
    availableHeight: 1,
  });
  const narrowSheetActive = narrowMetrics.active && workspace.mode === 'docked';
  const panelCollapsed =
    workspace.minimized || (narrowSheetActive && workspace.sheetState === 'peek');
  const relationships = index.relationshipsByPerson.get(person.id) ?? [];
  const counterparts = index.counterpartsByPerson.get(person.id) ?? [];
  const enabled = useMemo(() => new Set(enabledRelationshipFilters), [enabledRelationshipFilters]);
  const availableFilters = useMemo(
    () =>
      RELATIONSHIP_FILTERS.filter((filter) =>
        relationships.some(
          (relationship) => relationshipFilterForPerson(relationship, person.id) === filter,
        ),
      ),
    [person.id, relationships],
  );
  const filterCounts = useMemo(
    () =>
      new Map(
        availableFilters.map((filter) => [
          filter,
          new Set(
            relationships
              .filter(
                (relationship) => relationshipFilterForPerson(relationship, person.id) === filter,
              )
              .map((relationship) =>
                relationship.source === person.id ? relationship.target : relationship.source,
              ),
          ).size,
        ]),
      ),
    [availableFilters, person.id, relationships],
  );
  const visibleCounterparts = useMemo(
    () => filteredCounterparts(counterparts, person.id, enabled),
    [counterparts, enabled, person.id],
  );
  const allAvailableEnabled = availableFilters.every((filter) => enabled.has(filter));

  useEffect(() => {
    const filterList = filterListRef.current;
    if (filterList === null) {
      return;
    }
    const updateContinuation = () => {
      const overflow = filterList.scrollWidth > filterList.clientWidth + 1;
      const atEnd = filterList.scrollLeft + filterList.clientWidth >= filterList.scrollWidth - 2;
      setFilterContinuationVisible(overflow && !atEnd);
    };
    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateContinuation);
    resizeObserver?.observe(filterList);
    filterList.addEventListener('scroll', updateContinuation, { passive: true });
    updateContinuation();
    return () => {
      resizeObserver?.disconnect();
      filterList.removeEventListener('scroll', updateContinuation);
    };
  }, [availableFilters.length, narrowSheetActive]);

  useEffect(() => {
    originRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    return () => {
      interactionCleanupRef.current?.();
      if (originRef.current?.isConnected === true) {
        originRef.current.focus();
      }
    };
  }, []);

  useEffect(() => {
    if (!panelCollapsed) {
      headingRef.current?.focus();
    }
  }, [panelCollapsed, person.id]);

  useEffect(() => {
    if (interactionRef.current === null) {
      liveRectRef.current = workspace.rect;
      setLiveRect(workspace.rect);
    }
  }, [workspace.rect]);

  useEffect(() => {
    const parent = panelRef.current?.parentElement ?? null;
    const updateMetrics = () => {
      const next = narrowWorkspaceMetrics(parent);
      setNarrowMetrics((current) =>
        current.active === next.active && current.availableHeight === next.availableHeight
          ? current
          : next,
      );
      notifyDossierLayout();
    };
    const visualViewport = window.visualViewport;
    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateMetrics);
    const header = document.querySelector<HTMLElement>('.site-header');
    if (header !== null) {
      resizeObserver?.observe(header);
    }
    if (parent !== null) {
      resizeObserver?.observe(parent);
    }
    window.addEventListener('resize', updateMetrics);
    visualViewport?.addEventListener('resize', updateMetrics);
    visualViewport?.addEventListener('scroll', updateMetrics);
    updateMetrics();
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateMetrics);
      visualViewport?.removeEventListener('resize', updateMetrics);
      visualViewport?.removeEventListener('scroll', updateMetrics);
    };
  }, []);

  useEffect(() => {
    if (workspace.mode !== 'floating') return;
    const keepInViewport = () => {
      const current = liveRectRef.current ?? defaultFloatingDossierRect(visibleViewport());
      const next = clampDossierRect(current, visibleViewport());
      if (!dossierRectsEqual(current, next)) {
        liveRectRef.current = next;
        setLiveRect(next);
        onWorkspaceChange({ ...workspace, rect: next });
        setWorkspaceStatus('Panel kept inside the visible screen.');
      }
      notifyDossierLayout();
    };
    const visualViewport = window.visualViewport;
    window.addEventListener('resize', keepInViewport);
    visualViewport?.addEventListener('resize', keepInViewport);
    visualViewport?.addEventListener('scroll', keepInViewport);
    keepInViewport();
    return () => {
      window.removeEventListener('resize', keepInViewport);
      visualViewport?.removeEventListener('resize', keepInViewport);
      visualViewport?.removeEventListener('scroll', keepInViewport);
    };
  }, [onWorkspaceChange, workspace]);

  const toggleFilter = (filter: RelationshipFilter) => {
    const next = new Set(enabled);
    if (next.has(filter)) {
      next.delete(filter);
    } else {
      next.add(filter);
    }
    onSetRelationshipFilters(RELATIONSHIP_FILTERS.filter((candidate) => next.has(candidate)));
  };

  const updateLiveRect = (rect: DossierRect) => {
    liveRectRef.current = rect;
    setLiveRect(rect);
  };

  const commitWorkspace = (nextWorkspace: DossierWorkspaceState, status: string): void => {
    if (nextWorkspace.rect !== null) {
      updateLiveRect(nextWorkspace.rect);
    }
    onWorkspaceChange(nextWorkspace);
    setWorkspaceStatus(status);
    notifyDossierLayout();
  };

  const floatOrDock = () => {
    if (workspace.mode === 'floating') {
      commitWorkspace(
        { ...workspace, mode: 'docked', pinned: false },
        'Panel docked in its responsive position.',
      );
      return;
    }
    const viewport = visibleViewport();
    const sourceRect =
      workspace.rect ??
      (panelRef.current === null
        ? defaultFloatingDossierRect(viewport)
        : elementRect(panelRef.current));
    commitWorkspace(
      {
        ...workspace,
        mode: 'floating',
        pinned: false,
        rect: clampDossierRect(sourceRect, viewport),
      },
      'Panel floating. Drag or use the move control arrow keys to reposition it.',
    );
  };

  const togglePin = () => {
    commitWorkspace(
      { ...workspace, pinned: !workspace.pinned },
      workspace.pinned
        ? 'Panel unpinned and ready to move or resize.'
        : 'Panel pinned at its current position and size.',
    );
  };

  const toggleMinimized = () => {
    if (narrowSheetActive) {
      const nextState = panelCollapsed ? 'half' : 'peek';
      commitWorkspace(
        preferDossierSheetState({ ...workspace, minimized: false }, nextState),
        nextState === 'peek'
          ? 'Selected Person sheet moved to Peek.'
          : 'Selected Person sheet moved to Half.',
      );
      return;
    }
    commitWorkspace(
      { ...workspace, minimized: !workspace.minimized },
      workspace.minimized ? 'Panel expanded.' : 'Panel minimized.',
    );
  };

  const resetWorkspace = () => {
    const rect = defaultFloatingDossierRect(visibleViewport());
    commitWorkspace(
      { ...workspace, rect },
      'Panel position and size reset inside the visible screen.',
    );
  };

  const setSheetState = (state: DossierSheetState, status?: string) => {
    commitWorkspace(
      preferDossierSheetState({ ...workspace, minimized: false }, state),
      status ?? `Selected Person sheet moved to ${state[0]?.toUpperCase()}${state.slice(1)}.`,
    );
  };

  const beginSheetInteraction = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (!narrowSheetActive || event.button !== 0) {
      return;
    }
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    sheetInteractionRef.current = {
      pointerId: event.pointerId,
      startY: event.clientY,
      startState: workspace.sheetState,
      currentState: workspace.sheetState,
    };
  };

  const continueSheetInteraction = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const interaction = sheetInteractionRef.current;
    if (interaction === null || interaction.pointerId !== event.pointerId) {
      return;
    }
    event.preventDefault();
    const stateIndex = DOSSIER_SHEET_STATES.indexOf(interaction.startState);
    const stepDelta = Math.trunc((interaction.startY - event.clientY) / 56);
    const nextState =
      DOSSIER_SHEET_STATES[
        Math.min(Math.max(stateIndex + stepDelta, 0), DOSSIER_SHEET_STATES.length - 1)
      ] ?? interaction.startState;
    if (nextState !== interaction.currentState) {
      interaction.currentState = nextState;
      setSheetState(nextState);
    }
  };

  const finishSheetInteraction = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const interaction = sheetInteractionRef.current;
    if (interaction === null || interaction.pointerId !== event.pointerId) {
      return;
    }
    sheetInteractionRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const adjustSheetWithKeyboard = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (!narrowSheetActive) {
      return;
    }
    let nextState: DossierSheetState | null = null;
    if (event.key === 'ArrowUp' || event.key === 'PageUp') {
      nextState = adjacentDossierSheetState(workspace.sheetState, 'expand');
    } else if (event.key === 'ArrowDown' || event.key === 'PageDown') {
      nextState = adjacentDossierSheetState(workspace.sheetState, 'collapse');
    } else if (event.key === 'Home') {
      nextState = 'peek';
    } else if (event.key === 'End') {
      nextState = 'full';
    }
    if (nextState !== null) {
      event.preventDefault();
      event.stopPropagation();
      setSheetState(nextState);
    }
  };

  const beginWorkspaceInteraction = (
    kind: WorkspaceInteraction['kind'],
    event: ReactPointerEvent<HTMLButtonElement>,
  ) => {
    if (
      event.button !== 0 ||
      workspace.mode !== 'floating' ||
      workspace.pinned ||
      (kind === 'resize' && workspace.minimized)
    ) {
      return;
    }
    const startRect =
      liveRectRef.current ??
      (panelRef.current === null
        ? defaultFloatingDossierRect(visibleViewport())
        : clampDossierRect(elementRect(panelRef.current), visibleViewport()));
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    interactionRef.current = {
      kind,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startRect,
    };
    panelRef.current?.setAttribute('data-adjusting', 'true');
    const target = event.currentTarget;
    const continueInteraction = (pointerEvent: PointerEvent) => {
      const interaction = interactionRef.current;
      if (interaction === null || interaction.pointerId !== pointerEvent.pointerId) return;
      pointerEvent.preventDefault();
      const deltaX = pointerEvent.clientX - interaction.startX;
      const deltaY = pointerEvent.clientY - interaction.startY;
      updateLiveRect(
        interaction.kind === 'move'
          ? moveDossierRect(interaction.startRect, deltaX, deltaY, visibleViewport())
          : resizeDossierRect(interaction.startRect, deltaX, deltaY, visibleViewport()),
      );
    };
    const finishInteraction = (pointerEvent: PointerEvent) => {
      const interaction = interactionRef.current;
      if (interaction === null || interaction.pointerId !== pointerEvent.pointerId) return;
      interactionRef.current = null;
      interactionCleanupRef.current?.();
      if (target.hasPointerCapture(pointerEvent.pointerId)) {
        target.releasePointerCapture(pointerEvent.pointerId);
      }
      panelRef.current?.removeAttribute('data-adjusting');
      const rect = liveRectRef.current;
      if (rect !== null) {
        commitWorkspace(
          { ...workspace, rect },
          interaction.kind === 'move'
            ? 'Panel moved to its new position.'
            : `Panel resized to ${Math.round(rect.width)} by ${Math.round(rect.height)} pixels.`,
        );
      }
    };
    const cleanup = () => {
      window.removeEventListener('pointermove', continueInteraction, true);
      window.removeEventListener('pointerup', finishInteraction, true);
      window.removeEventListener('pointercancel', finishInteraction, true);
      interactionCleanupRef.current = null;
    };
    interactionCleanupRef.current?.();
    interactionCleanupRef.current = cleanup;
    window.addEventListener('pointermove', continueInteraction, { capture: true, passive: false });
    window.addEventListener('pointerup', finishInteraction, true);
    window.addEventListener('pointercancel', finishInteraction, true);
  };

  const adjustWorkspaceWithKeyboard = (
    kind: WorkspaceInteraction['kind'],
    event: ReactKeyboardEvent<HTMLButtonElement>,
  ) => {
    if (
      workspace.mode !== 'floating' ||
      workspace.pinned ||
      (kind === 'resize' && workspace.minimized) ||
      !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)
    ) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const step = event.shiftKey ? 40 : 10;
    const horizontal = event.key === 'ArrowLeft' ? -step : event.key === 'ArrowRight' ? step : 0;
    const vertical = event.key === 'ArrowUp' ? -step : event.key === 'ArrowDown' ? step : 0;
    const current = liveRectRef.current ?? defaultFloatingDossierRect(visibleViewport());
    const rect =
      kind === 'move'
        ? moveDossierRect(current, horizontal, vertical, visibleViewport())
        : resizeDossierRect(current, horizontal, vertical, visibleViewport());
    commitWorkspace(
      { ...workspace, rect },
      kind === 'move'
        ? `Panel moved to ${Math.round(rect.left)} pixels from the left and ${Math.round(
            rect.top,
          )} pixels from the top.`
        : `Panel resized to ${Math.round(rect.width)} by ${Math.round(rect.height)} pixels.`,
    );
  };

  const panelStyle: CSSProperties | undefined =
    workspace.mode === 'floating' && liveRect !== null
      ? {
          left: liveRect.left,
          top: liveRect.top,
          width: liveRect.width,
          height: workspace.minimized ? undefined : liveRect.height,
        }
      : narrowSheetActive
        ? {
            height: dossierSheetHeight(workspace.sheetState, narrowMetrics.availableHeight),
          }
        : undefined;

  return (
    <aside
      ref={panelRef}
      className="dossier"
      aria-label={`${person.name} connection dossier`}
      data-minimized={panelCollapsed}
      data-pinned={workspace.pinned}
      data-sheet-explicit={workspace.sheetStateExplicit}
      data-sheet-state={narrowSheetActive ? workspace.sheetState : undefined}
      data-workspace-mode={workspace.mode}
      style={panelStyle}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          if (canGoBack) {
            onBack();
          } else {
            onClose();
          }
        }
      }}
    >
      <p id="dossier-workspace-instructions" className="sr-only">
        While the panel is floating and unpinned, use arrow keys on the move control to reposition
        it. Use arrow keys on the resize control to change its width and height. Hold Shift for
        larger steps.
      </p>
      <p className="sr-only" role="status" aria-live="polite">
        {workspaceStatus}
      </p>
      {narrowSheetActive ? (
        <div className="dossier__sheet-controls">
          <button
            className="dossier__sheet-handle"
            type="button"
            aria-label={`Resize Selected Person sheet. Current state ${workspace.sheetState}. Swipe up or down, or use Up and Down arrow keys.`}
            onKeyDown={adjustSheetWithKeyboard}
            onPointerDown={beginSheetInteraction}
            onPointerMove={continueSheetInteraction}
            onPointerUp={finishSheetInteraction}
            onPointerCancel={finishSheetInteraction}
          >
            <span aria-hidden="true" />
            <strong>{workspace.sheetState}</strong>
          </button>
          <div className="dossier__sheet-options" role="group" aria-label="Sheet height">
            {DOSSIER_SHEET_STATES.map((state) => (
              <button
                key={state}
                type="button"
                aria-pressed={workspace.sheetState === state}
                onClick={() => setSheetState(state)}
              >
                {state}
              </button>
            ))}
          </div>
        </div>
      ) : null}
      <div className="dossier__workspace-bar" role="toolbar" aria-label="Selected Person workspace">
        {workspace.mode === 'floating' ? (
          <button
            className="dossier__move-handle"
            type="button"
            aria-label={
              workspace.pinned ? 'Selected Person panel is pinned' : 'Move Selected Person panel'
            }
            aria-describedby="dossier-workspace-instructions"
            disabled={workspace.pinned}
            title={
              workspace.pinned
                ? 'Unpin the panel to move it'
                : 'Drag to move. Arrow keys also move the panel.'
            }
            onKeyDown={(event) => adjustWorkspaceWithKeyboard('move', event)}
            onPointerDown={(event) => beginWorkspaceInteraction('move', event)}
          >
            <span className="dossier__move-dots" aria-hidden="true">
              <i />
              <i />
              <i />
              <i />
              <i />
              <i />
            </span>
            <span className="dossier__workspace-label">
              <span>Selected person</span>
              {panelCollapsed ? <strong>{person.name}</strong> : <small>Floating</small>}
            </span>
          </button>
        ) : (
          <div className="dossier__workspace-title">
            <span className="dossier__workspace-label">
              <span>Selected person</span>
              {panelCollapsed ? <strong>{person.name}</strong> : <small>Docked</small>}
            </span>
          </div>
        )}
        <div className="dossier__workspace-actions">
          {workspace.mode === 'floating' ? (
            <button
              className="dossier__workspace-action"
              type="button"
              aria-label={workspace.pinned ? 'Unpin panel' : 'Pin panel'}
              aria-pressed={workspace.pinned}
              title={workspace.pinned ? 'Unpin panel' : 'Pin panel here'}
              onClick={togglePin}
            >
              <WorkspaceIcon name={workspace.pinned ? 'unpin' : 'pin'} />
            </button>
          ) : null}
          <button
            className="dossier__workspace-action"
            type="button"
            aria-label={workspace.mode === 'floating' ? 'Dock panel' : 'Float panel'}
            title={workspace.mode === 'floating' ? 'Return to responsive dock' : 'Float panel'}
            onClick={floatOrDock}
          >
            <WorkspaceIcon name={workspace.mode === 'floating' ? 'dock' : 'float'} />
          </button>
          {panelCollapsed || narrowSheetActive ? (
            <button
              className="dossier__workspace-action"
              type="button"
              aria-label={viewMode === 'graph' ? 'Switch to List view' : 'Switch to Graph view'}
              title={viewMode === 'graph' ? 'Switch to List view' : 'Switch to Graph view'}
              onClick={() => onViewModeChange(viewMode === 'graph' ? 'list' : 'graph')}
            >
              <WorkspaceIcon name={viewMode === 'graph' ? 'list' : 'graph'} />
            </button>
          ) : null}
          <button
            className="dossier__workspace-action"
            type="button"
            aria-label={
              narrowSheetActive
                ? panelCollapsed
                  ? 'Expand sheet to Half'
                  : 'Collapse sheet to Peek'
                : workspace.minimized
                  ? 'Expand panel'
                  : 'Minimize panel'
            }
            aria-expanded={!panelCollapsed}
            title={
              narrowSheetActive
                ? panelCollapsed
                  ? 'Expand sheet to Half'
                  : 'Collapse sheet to Peek'
                : workspace.minimized
                  ? 'Expand panel'
                  : 'Minimize panel'
            }
            onClick={toggleMinimized}
          >
            <WorkspaceIcon name={panelCollapsed ? 'expand' : 'minimize'} />
          </button>
          {workspace.mode === 'floating' && !panelCollapsed ? (
            <button
              className="dossier__workspace-action"
              type="button"
              aria-label="Reset panel position and size"
              title="Reset panel position and size"
              onClick={resetWorkspace}
            >
              <WorkspaceIcon name="reset" />
            </button>
          ) : null}
          <button
            className="dossier__workspace-action"
            type="button"
            aria-label="Close dossier"
            title="Close Selected Person"
            onClick={onClose}
          >
            <WorkspaceIcon name="close" />
          </button>
        </div>
      </div>

      {panelCollapsed ? null : (
        <div className="dossier__viewport">
          <header className="dossier__header">
            <div>
              {person.inclusionRole === 'context' ? (
                <p className="dossier__context">
                  <span className="context-badge">Connection context</span>
                </p>
              ) : null}
              <h2 ref={headingRef} tabIndex={-1}>
                {person.name}
              </h2>
              {person.description === undefined ? null : (
                <p className="dossier__description">{person.description}</p>
              )}
              {constellationSummary === null ? null : (
                <section
                  className="person-constellation-summary"
                  aria-label="Constellation summary"
                >
                  <dl>
                    <div>
                      <dt>Connections</dt>
                      <dd>
                        {visibleCounterparts.length.toLocaleString('en-US')}
                        {visibleCounterparts.length === counterparts.length
                          ? ''
                          : ` / ${counterparts.length.toLocaleString('en-US')}`}
                      </dd>
                    </div>
                    <div>
                      <dt>Personal</dt>
                      <dd>
                        {constellationSummary.personalCounterpartCount.toLocaleString('en-US')}
                      </dd>
                    </div>
                    <div>
                      <dt>Family</dt>
                      <dd>{constellationSummary.familyCounterpartCount.toLocaleString('en-US')}</dd>
                    </div>
                    <div>
                      <dt>Connected web</dt>
                      <dd>{constellationSummary.componentSize.toLocaleString('en-US')}</dd>
                    </div>
                  </dl>
                </section>
              )}
            </div>
          </header>

          <section className="dossier__view-controls" aria-label="Selected person view">
            <div className="dossier__filter-row">
              <p className="eyebrow">Relationship filters</p>
              <div
                className="relationship-filter-strip"
                data-continuation={filterContinuationVisible}
              >
                <div ref={filterListRef} className="relationship-filters">
                  <button
                    className="dossier__filter-reset"
                    type="button"
                    aria-pressed={allAvailableEnabled}
                    onClick={() => onSetRelationshipFilters(RELATIONSHIP_FILTERS)}
                  >
                    All
                  </button>
                  {availableFilters.map((filter) => (
                    <button
                      key={filter}
                      type="button"
                      aria-pressed={enabled.has(filter)}
                      onClick={() => toggleFilter(filter)}
                    >
                      <span>{RELATIONSHIP_FILTER_LABELS[filter]}</span>
                      <small>{filterCounts.get(filter) ?? 0}</small>
                    </button>
                  ))}
                </div>
                <span className="relationship-filter-strip__cue" aria-hidden="true">
                  More <span>→</span>
                </span>
              </div>
            </div>
            <div className="dossier__command-row">
              <div className="focus-depth">
                <span>View</span>
                <div className="focus-depth__options">
                  {FOCUS_DEPTHS.map((depth) => (
                    <button
                      key={depth}
                      type="button"
                      aria-pressed={focusDepth === depth}
                      onClick={() => onSetFocusDepth(depth)}
                    >
                      {FOCUS_DEPTH_LABELS[depth]}
                    </button>
                  ))}
                </div>
              </div>
              <div className="dossier__actions" aria-label="Exploration actions">
                {canGoBack ? (
                  <button className="button button--small" type="button" onClick={onBack}>
                    Back
                  </button>
                ) : null}
                <button
                  className="button button--small"
                  type="button"
                  aria-pressed={favorite}
                  onClick={onToggleFavorite}
                >
                  {favorite ? 'Remove from Saved' : 'Save person'}
                </button>
                <button
                  className="button button--gold dossier__connect"
                  type="button"
                  onClick={() => onConnect(person.id)}
                >
                  Connect {person.name} to{'\u2026'}
                </button>
              </div>
            </div>
          </section>

          <div className="dossier__relationships">
            {counterparts.length === 0 ? (
              <section className="connection-empty">
                <h3>No relationship in this snapshot</h3>
                <p>
                  A missing link means only that this data snapshot did not contain one. Try another
                  connected web below.
                </p>
              </section>
            ) : visibleCounterparts.length === 0 ? (
              <section className="connection-empty">
                <h3>No connections match these filters</h3>
                <p>
                  Enable a relationship type above to bring its people and constellation lines back.
                </p>
              </section>
            ) : (
              <section className="connection-group" aria-label="Source-linked connections">
                <h3>
                  Connections <span>{visibleCounterparts.length.toLocaleString('en-US')}</span>
                </h3>
                {visibleCounterparts.map(
                  ({ counterpart, phases, representative, relationshipLabels }) => {
                    const otherPerson = index.peopleById.get(counterpart.personId);
                    if (otherPerson === undefined) return null;
                    const singlePhase = phases.length === 1;
                    return (
                      <article
                        className="connection-entry"
                        data-single-phase={singlePhase}
                        key={counterpart.personId}
                      >
                        <button
                          className="connection-row"
                          type="button"
                          onClick={() => onSelect(counterpart.personId)}
                        >
                          <span
                            className={`connection-row__dot connection-row__dot--${relationshipTone(representative)}`}
                            aria-hidden="true"
                          />
                          <span className="connection-row__person">{otherPerson.name}</span>
                          <span className="connection-row__meta">
                            {relationshipLabels.join(' \u00b7 ')}
                          </span>
                        </button>
                        <ol
                          className="connection-phases"
                          aria-label={`Relationship details for ${otherPerson.name}`}
                        >
                          {phases.map((relationship) => {
                            const dateLabel = relationshipDateLabel(relationship, snapshotDate);
                            const note = usefulRelationshipNote(relationship, person.id);
                            const sourceGroups = groupSourcesForDisplay(
                              sourcesForRelationship(relationship, index),
                            );
                            const sourceCount = sourceGroups.reduce(
                              (total, group) => total + group.links.length,
                              0,
                            );
                            return (
                              <li className="connection-phase" key={relationship.id}>
                                <div className="connection-phase__summary">
                                  {singlePhase ? null : (
                                    <strong>
                                      {relationshipLabelForPerson(relationship, person.id)}
                                    </strong>
                                  )}
                                  {dateLabel === null ? null : <span>{dateLabel}</span>}
                                  <small>{relationshipProvenanceLabel(relationship)}</small>
                                  {note === null ? null : <span>{note}</span>}
                                </div>
                                {sourceCount === 0 ? null : (
                                  <details className="connection-sources">
                                    <summary>
                                      Sources ({sourceCount.toLocaleString('en-US')})
                                    </summary>
                                    <dl>
                                      {sourceGroups.map((group) => (
                                        <div key={group.label}>
                                          <dt>{group.label}</dt>
                                          <dd>
                                            {group.links.map((link) => (
                                              <a
                                                key={link.id}
                                                href={link.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title={`${link.sourceClass} source`}
                                              >
                                                {link.label}
                                              </a>
                                            ))}
                                          </dd>
                                        </div>
                                      ))}
                                    </dl>
                                  </details>
                                )}
                              </li>
                            );
                          })}
                        </ol>
                      </article>
                    );
                  },
                )}
              </section>
            )}
          </div>

          {suggestions.length === 0 ? null : (
            <section className="dossier__next" aria-label="Ideas for where to explore next">
              <p className="eyebrow">Keep exploring</p>
              <div>
                {suggestions.map((suggestion) => (
                  <button type="button" key={suggestion.id} onClick={() => onSelect(suggestion.id)}>
                    {suggestion.name}
                  </button>
                ))}
              </div>
            </section>
          )}

          <p className="dossier__fixture-note">
            {fixture
              ? 'Reference fixture only. Production dossiers retain provenance for every relationship.'
              : (limitations?.[0] ??
                'Source details identify upstream evidence; claims are not independently verified.')}
          </p>
        </div>
      )}

      {workspace.mode === 'floating' && !workspace.pinned && !panelCollapsed ? (
        <button
          className="dossier__resize-handle"
          type="button"
          aria-label="Resize Selected Person panel"
          aria-describedby="dossier-workspace-instructions"
          title="Drag to resize width and height. Arrow keys also resize."
          onKeyDown={(event) => adjustWorkspaceWithKeyboard('resize', event)}
          onPointerDown={(event) => beginWorkspaceInteraction('resize', event)}
        >
          <span aria-hidden="true" />
        </button>
      ) : null}
    </aside>
  );
}
